/**
 * 视频区：本地预览 + 远端视频/屏幕共享。
 */

import { useEffect, useRef, useState } from 'react'

import type { RemoteTrack } from '../lib/media'
import { applySpeaker } from '../lib/devices'
import { ControlOverlay, ControlRequestButton } from './RemoteControl'
import { hasPerm, Perm, USER_NONE } from '../lib/protocol'
import { isAnnotPage, useWhiteboardStore } from '../stores/whiteboardStore'
import { ShareAnnotate } from './ShareAnnotate'
import { IcMicOff } from './Icons'
import { useRoomStore } from '../stores/roomStore'
import { Avatar } from './Avatar'
import { PIN_SELF, pickSpeaker, useLayoutStore } from '../stores/layoutStore'
import { loadPos, savePos, toPx, toRatio, type Pos } from '../lib/dragCorner'

export function VideoStage({ localStream }: { localStream?: MediaStream | null }) {
  // 共享画面的 video 元素：远程控制要用它换算坐标。
  const [screenEl, setScreenEl] = useState<HTMLVideoElement | null>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const remoteTracks = useRoomStore((s) => s.remoteTracks)
  const members = useRoomStore((s) => s.members)
  const sharerId = useRoomStore((s) => s.sharerId)
  const sharePaused = useRoomStore((s) => s.sharePaused)
  const selfId = useRoomStore((s) => s.selfId)
  const speaking = useRoomStore((s) => s.speaking)
  const layout = useLayoutStore((s) => s.layout)
  const pinnedId = useLayoutStore((s) => s.pinnedId)
  const setLayout = useLayoutStore((s) => s.setLayout)
  const togglePin = useLayoutStore((s) => s.togglePin)
  const [isFull, setIsFull] = useState(false)

  useEffect(() => {
    const onChange = () => setIsFull(document.fullscreenElement === stageRef.current)
    document.addEventListener('fullscreenchange', onChange)
    // Shift+F 的快捷键在工具栏里绑（那里集中管所有键位），但全屏要拿到视频区这个
    // DOM 节点才能做，所以走一个自定义事件把动作转回来。
    const onKeyFull = () => {
      const el = stageRef.current
      if (!el) return
      if (document.fullscreenElement) void document.exitFullscreen()
      else void el.requestFullscreen?.()
    }
    window.addEventListener('whatylive.toggleFull', onKeyFull)
    return () => {
      document.removeEventListener('fullscreenchange', onChange)
      window.removeEventListener('whatylive.toggleFull', onKeyFull)
    }
  }, [])

  const tracks = [...remoteTracks.values()]
  const screen = tracks.find((t) => t.kind === 'screen')

  // 共享者自己的画面。共享的人收不到自己的流（SFU 不回环），原本界面上一片空白 ——
  // 而「共享时批注」的主要用户恰恰是他：要在画面上圈画就得先看得到画面。
  // 回放本地流，无额外带宽也无延迟。
  const media = useRoomStore((s) => s.media)
  const iAmSharing = sharerId === selfId && sharerId !== USER_NONE
  const [ownScreen, setOwnScreen] = useState<MediaStream | null>(null)
  useEffect(() => {
    if (!iAmSharing || !media) {
      setOwnScreen(null)
      return
    }
    // 共享刚开始时轨还没建好，稍等一拍再取。
    const id = window.setTimeout(() => setOwnScreen(media.localScreenStream()), 300)
    return () => window.clearTimeout(id)
  }, [iAmSharing, media])

  // 共享批注。批注层是服务端在共享开始时追加的那一页（标题固定），共享结束会被删掉。
  const [annotating, setAnnotating] = useState(false)
  const wbPages = useWhiteboardStore((s) => s.pages)
  const annotPage = wbPages.find((p) => isAnnotPage(p))
  const client = useRoomStore((s) => s.client)
  const me = members.get(selfId)
  // 谁能批注：共享的人自己，以及主持人（他要能在学员共享的画面上指点）。
  // 还要有白板权限 —— 批注走的就是白板那套对象。
  const canAnnotate =
    !!annotPage &&
    !!me &&
    hasPerm(me.prop, Perm.Whiteboard) &&
    (sharerId === selfId || hasPerm(me.prop, Perm.Op))

  // 共享一停批注层就没了，此时必须退出批注模式 —— 否则留着一个空画布挡在画面上。
  //
  // 「还在共享」要同时看 ownScreen：共享者本人收不到自己的远端屏幕轨（SFU 不回环），
  // 对他来说 screen 恒为空。只看 screen 的话，他每画一笔（批注页对象变了、副作用重跑）
  // 批注就被关掉 —— 而他恰恰是这个功能的主要用户。依赖用布尔值而不是对象：
  // 这里关心的是「还在不在」，页面内容变化不该触发它。
  const showingShare = !!screen || !!ownScreen
  const hasAnnotPage = !!annotPage
  useEffect(() => {
    if (!showingShare || !hasAnnotPage) setAnnotating(false)
  }, [showingShare, hasAnnotPage])
  const cams = tracks.filter((t) => t.kind === 'video')
  // 自己的画面也是一格。老版本把它做成右下角的悬浮小窗，
  // 于是出现过「自己开着摄像头，主区却写着『暂无视频画面』」这种自相矛盾的画面。
  const hasLocal = !!localStream && localStream.getVideoTracks().length > 0
  // 统一成「格」：自己用 PIN_SELF 当席位号，和远端一起参与置顶/演讲者挑选。
  const tiles: Tile[] = []
  if (hasLocal) tiles.push({ id: PIN_SELF, name: '我', self: true, stream: localStream! })
  for (const t of cams) tiles.push({ id: t.userId, name: members.get(t.userId)?.name ?? `席位 ${t.userId}`, self: false, stream: t.stream, key: t.trackId })
  // 没开摄像头的人也占一格，显示头像 —— 腾讯会议就是这样。
  // 让他们完全消失的话，视频页上「教室里有几个人」和成员列表对不上，
  // 老师会以为学生掉线了。
  const shown = new Set(tiles.map((t) => t.id))
  if (!hasLocal) shown.add(PIN_SELF)
  for (const m of members.values()) {
    if (m.id === selfId || shown.has(m.id)) continue
    tiles.push({ id: m.id, name: m.name || `席位 ${m.id}`, self: false, key: `noc-${m.id}` })
  }
  if (!hasLocal && members.has(selfId)) {
    tiles.unshift({ id: PIN_SELF, name: '我', self: true, key: 'noc-self' })
  }
  const tileCount = tiles.length
  // 音频不在这里播：本组件只在「视频」标签页挂载，
  // 而上课时大家多半看着白板 —— 音频交给常驻的 AudioSink。

  const speakerMode = layout === 'speaker' && !screen && tileCount > 0
  const mainId = speakerMode ? pickSpeaker(tiles.map((t) => t.id), pinnedId, speaking) : USER_NONE
  const mainTile = tiles.find((t) => t.id === mainId)
  const stripTiles = speakerMode ? tiles.filter((t) => t.id !== mainId) : tiles

  const toggleFull = () => {
    const el = stageRef.current
    if (!el) return
    if (document.fullscreenElement) void document.exitFullscreen()
    else void el.requestFullscreen?.()
  }

  // 谁的麦克风开着：远端看有没有音频轨到达，自己看本地流里有没有开着的音频轨。
  // 腾讯会议的格子左下角就是「名字 + 静音图标」，一眼能看出谁没开麦。
  const audioUsers = new Set(tracks.filter((x) => x.kind === 'audio').map((x) => x.userId))
  const selfMicOn = !!localStream?.getAudioTracks().some((a) => a.enabled && a.readyState === 'live')

  const renderTile = (t: Tile, big = false) => {
    const level = t.self ? 0 : (speaking.get(t.id) ?? 0)
    const pinned = pinnedId === t.id
    const micOn = t.self ? selfMicOn : audioUsers.has(t.id)
    const isHost = !t.self && hasPerm(members.get(t.id)?.prop ?? 0, Perm.Op)
    return (
      <div
        key={t.key ?? `self`}
        className={`cam-tile${t.self ? ' is-self' : ''}${level > 15 ? ' talking' : ''}${pinned ? ' pinned' : ''}`}
        data-user={t.id}
      >
        {t.stream ? (
          t.self ? <LocalVideo stream={t.stream} /> : <StreamVideo stream={t.stream} className="cam-view" />
        ) : (
          <span className="cam-novideo">
            <Avatar name={t.name} size={big ? 96 : 52} />
          </span>
        )}
        <span className="cam-name">
          {!micOn && <IcMicOff className="cam-mic-off" />}
          {t.name}
          {isHost && <span className="cam-host">主持</span>}
        </span>
        {!big || pinned ? (
          <button className="tile-pin" title={pinned ? '取消置顶' : '置顶到大图'} onClick={() => togglePin(t.id)}>
            {pinned ? '取消置顶' : '置顶'}
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div className={`stage${speakerMode ? ' speaker' : ''}`} ref={stageRef}>
      {tileCount > 0 && !screen && (
        <div className="stage-bar">
          <button className={layout === 'grid' ? 'on' : ''} onClick={() => setLayout('grid')}>宫格</button>
          <button className={layout === 'speaker' ? 'on' : ''} onClick={() => setLayout('speaker')}>演讲者</button>
          <span className="sep" />
          <button onClick={toggleFull} title="全屏 (Esc 退出)">{isFull ? '退出全屏' : '全屏'}</button>
        </div>
      )}
      {screen || ownScreen ? (
        <div className="stage-main">
          {screen ? (
            <Video track={screen} className="screen-view" onEl={setScreenEl} />
          ) : (
            <OwnScreenVideo stream={ownScreen!} />
          )}
          <ControlOverlay videoEl={screenEl} />
          {/* 共享方暂停了：画面定格在最后一帧，不说一声的话观众会以为卡了，反复刷新。
              共享方自己有工具栏上的「继续共享」，这层只给观众看。 */}
          {sharePaused && !iAmSharing && (
            <div className="share-paused-mask" role="status">共享已暂停，请稍候</div>
          )}
          {annotating && annotPage && client && (
            <ShareAnnotate page={annotPage} client={client} selfId={selfId} onClose={() => setAnnotating(false)} />
          )}
          <div className="stage-label">
            {iAmSharing
              ? '你正在共享屏幕'
              : `正在共享：${members.get(sharerId)?.name ?? (screen && members.get(screen.userId)?.name) ?? '未知'}`}
            {canAnnotate && !annotating && (
              <button className="stage-annot" onClick={() => setAnnotating(true)} title="在共享画面上圈画">
                批注
              </button>
            )}
            <ControlRequestButton />
          </div>
        </div>
      ) : speakerMode && mainTile ? (
        <div className="speaker-main">{renderTile(mainTile, true)}</div>
      ) : (
        tileCount === 0 && (
          <div className="stage-empty">
            <p>暂无视频画面</p>
            <span>开启摄像头或等待老师共享屏幕</span>
          </div>
        )
      )}

      {stripTiles.length > 0 && (
        <div
          className={screen || speakerMode ? 'cam-strip compact' : 'cam-grid'}
          data-count={Math.min(stripTiles.length, 16)}
        >
          {stripTiles.map((t) => renderTile(t))}
        </div>
      )}
    </div>
  )
}

interface Tile {
  id: number
  name: string
  self: boolean
  /** 没开摄像头时为空，格子里显示头像 */
  stream?: MediaStream
  key?: string
}

/** 远端视频：直接挂 MediaStream。 */
function StreamVideo({ stream, className }: { stream: MediaStream; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const el = ref.current
    if (el && el.srcObject !== stream) {
      el.srcObject = stream
      el.play().catch(() => {})
    }
  }, [stream])
  // 必须静音：声音只走 AudioSink（那里才按用户选的扬声器出声）。视频元素不静音的话，
  // 一旦流里混进音频就会和 AudioSink 双重放音，还绕过扬声器选择。
  return <video ref={ref} className={className} autoPlay playsInline muted />
}

/** 视频元素：把 MediaStream 挂上去并自动播放。 */
function Video({
  track,
  className,
  onEl,
}: {
  track: RemoteTrack
  className?: string
  onEl?: (el: HTMLVideoElement | null) => void
}) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const el = ref.current
    if (el && el.srcObject !== track.stream) {
      el.srcObject = track.stream
      el.play().catch(() => {})
    }
    onEl?.(el)
  }, [track.stream, onEl])
  // 静音的理由同 StreamVideo：声音统一由 AudioSink 播。静音播放也一定能过自动播放策略。
  return <video ref={ref} className={className} autoPlay playsInline muted />
}

/**
 * AudioSink 是常驻的音频出口。
 *
 * 必须独立于视频区：老师讲课时学生通常停在白板页，
 * 若音频元素随「视频」标签页一起卸载，就会出现「看得见板书听不到人」。
 */
export function AudioSink() {
  const remoteTracks = useRoomStore((s) => s.remoteTracks)
  const audios = [...remoteTracks.values()].filter((t) => t.kind === 'audio')
  return (
    <div className="audio-sink" aria-hidden>
      {audios.map((t) => (
        <Audio key={t.trackId} track={t} />
      ))}
    </div>
  )
}

/** 音频元素：只播不显示。 */
function Audio({ track }: { track: RemoteTrack }) {
  const ref = useRef<HTMLAudioElement>(null)
  const speaker = useRoomStore((s) => s.speakerId)
  useEffect(() => {
    const el = ref.current
    if (el && el.srcObject !== track.stream) {
      el.srcObject = track.stream
      el.play().catch(() => {})
    }
    if (el) void applySpeaker(el)
  }, [track.stream, speaker])
  return <audio ref={ref} autoPlay />
}

/** 宫格里自己的那一格。与角落小窗共用同一份挂流逻辑。 */
function LocalVideo({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const el = ref.current
    if (el && el.srcObject !== stream) {
      el.srcObject = stream
      el.play().catch(() => {})
    }
  }, [stream])
  return <video ref={ref} className="cam-view" autoPlay playsInline muted />
}

/**
 * 本地摄像头预览（角落小窗）。看白板时用，视频页里自己已经在宫格中。
 *
 * 可以拖：它默认停在右下角，而那里正是板书写到最后会用到的地方。
 * 讲课的人没法让自己的画面躲开，只能绕着它写 —— 所以给它一个能挪走的余地。
 * 落点按比例记在本机，换屏幕、拉窗口都不会把它甩到容器外面。
 */
export function LocalPreview({ stream }: { stream: MediaStream | null }) {
  const ref = useRef<HTMLVideoElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<Pos>(loadPos)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream
      el.play().catch(() => {})
    }
  }, [stream])

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const box = boxRef.current
    const parent = box?.offsetParent as HTMLElement | null
    if (!box || !parent) return
    const b = box.getBoundingClientRect()
    const p = parent.getBoundingClientRect()
    // 记住按下时指针在小窗内的位置，拖动时才不会「跳」到指针底下。
    const dx = e.clientX - b.left
    const dy = e.clientY - b.top
    box.setPointerCapture(e.pointerId)
    setDragging(true)

    const move = (ev: PointerEvent) => {
      const left = ev.clientX - p.left - dx
      const top = ev.clientY - p.top - dy
      setPos(toRatio(left, top, { w: p.width, h: p.height }, { w: b.width, h: b.height }))
    }
    const up = () => {
      setDragging(false)
      box.releasePointerCapture(e.pointerId)
      box.removeEventListener('pointermove', move)
      box.removeEventListener('pointerup', up)
      setPos((cur) => { savePos(cur); return cur })
    }
    box.addEventListener('pointermove', move)
    box.addEventListener('pointerup', up)
  }

  // 容器尺寸要等挂载后才知道，所以位置在这里算而不是在渲染时写死。
  const [px, setPx] = useState<{ left: number; top: number } | null>(null)
  useEffect(() => {
    const box = boxRef.current
    const parent = box?.offsetParent as HTMLElement | null
    if (!box || !parent) return
    const apply = () => {
      const p = parent.getBoundingClientRect()
      const b = box.getBoundingClientRect()
      setPx(toPx(pos, { w: p.width, h: p.height }, { w: b.width, h: b.height }))
    }
    apply()
    // 收起侧栏、拉窗口都会改变容器宽高，位置得跟着重算，
    // 否则「贴着右下角」会变成「悬在中间」甚至跑到容器外。
    const ro = new ResizeObserver(apply)
    ro.observe(parent)
    return () => ro.disconnect()
  }, [pos, stream])

  if (!stream || stream.getVideoTracks().length === 0) return null
  return (
    <div
      ref={boxRef}
      className={`local-preview${dragging ? ' dragging' : ''}`}
      style={px ? { left: px.left, top: px.top, right: 'auto', bottom: 'auto' } : undefined}
      onPointerDown={onPointerDown}
      title="可以拖动，换个位置放"
    >
      {/* 本地预览必须静音，否则会听到自己的回声 */}
      <video ref={ref} autoPlay playsInline muted />
      <span className="cam-name">我</span>
    </div>
  )
}

export const NO_USER = USER_NONE

/** 共享者自己的屏幕预览。muted 必须给：回放自己的流会形成啸叫（这里其实只有视频轨，但保险）。 */
function OwnScreenVideo({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.srcObject = stream
    void el.play().catch(() => {})
  }, [stream])
  return <video ref={ref} className="screen-view" autoPlay playsInline muted />
}
